import React from 'react';
import { AREA_COLORS, getUsedColors, isColorAvailable } from '../../config/areaColors';

/**
 * Area Color Picker Component
 * Displays a 4x3 grid of pastel colors for area selection
 * Blocks already-used colors with diagonal cross overlay
 *
 * @param {string} currentColor - Currently selected color hex (#RRGGBB)
 * @param {Array} userAreas - All user areas to check for used colors
 * @param {Function} onColorSelect - Callback when color is selected
 * @param {Function} onClose - Callback to close picker
 */
const AreaColorPicker = ({
  currentColor,
  userAreas,
  onColorSelect,
  onClose,
  containerStyle = {}
}) => {
  const usedColors = getUsedColors(userAreas);

  const handleColorClick = (colorHex) => {
    const isUsed = !isColorAvailable(colorHex, usedColors);
    const isCurrent = currentColor?.toUpperCase() === colorHex.toUpperCase();

    // Allow selection if color is available OR if it's the current color (re-selecting)
    if (!isUsed || isCurrent) {
      onColorSelect(colorHex);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        right: '80px', // Position to the right of the color section
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10000,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '3px',
        width: '133px',  // 4 columns × 28px (112px) + 3 gaps × 3px (9px) + padding 6px×2 (12px) = 133px
        ...containerStyle // Allow style overrides
      }}
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside picker
    >
      {AREA_COLORS.map(color => {
        const isUsed = !isColorAvailable(color.hex, usedColors);
        const isCurrent = currentColor?.toUpperCase() === color.hex.toUpperCase();
        const isClickable = !isUsed || isCurrent;

        return (
          <button
            key={color.id}
            disabled={!isClickable}
            onClick={() => handleColorClick(color.hex)}
            style={{
              width: '28px',
              height: '28px',
              background: color.hex,
              border: isCurrent ? '1.5px solid #024B99' : '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: isClickable ? 'pointer' : 'not-allowed',
              position: 'relative',
              opacity: !isClickable ? 0.4 : 1,
              transition: 'all 0.2s',
              padding: 0
            }}
            onMouseOver={(e) => {
              if (isClickable && !isCurrent) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            title={`${color.name}${isUsed && !isCurrent ? ' (In use)' : ''}`}
          >
            {/* Diagonal cross for used colors */}
            {isUsed && !isCurrent && (
              <>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '1.5px',
                  background: '#ef4444',
                  transform: 'translateY(-50%) rotate(45deg)',
                  transformOrigin: 'center',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '1.5px',
                  background: '#ef4444',
                  transform: 'translateY(-50%) rotate(-45deg)',
                  transformOrigin: 'center',
                  pointerEvents: 'none'
                }} />
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default AreaColorPicker;
